// src/pages/AttendanceDashboard.jsx — Unified Attendance & History Dashboard
import React, { useEffect, useState, useCallback } from 'react';
import { Nav } from 'react-bootstrap';
import api from '../services/api';
import toast from 'react-hot-toast';
import { RefreshCw, Users, Clock } from 'lucide-react';
import './AttendanceDashboard.css';
import './TeamPortfolio.css';
import { useAuth } from '../context/AuthContext';

export default function AttendanceDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('live'); // 'live' | 'history'

  // Live Status State
  const [employees, setEmployees] = useState([]);
  const [loadingLive, setLoadingLive] = useState(true);

  // History State
  const [allEmployees, setAllEmployees] = useState([]);
  const [historyEmpId, setHistoryEmpId] = useState('');
  const [startDate, setStartDate]       = useState('');
  const [endDate, setEndDate]           = useState('');
  const [records, setRecords]           = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load Live Attendance Data
  const handleResetBreak = async (empId) => {
    if (!window.confirm("Are you sure you want to reset this employee's break hours to 0?")) return;
    try {
      await api.post(`/attendance/reset-break/${empId}`);
      toast.success("Break time reset!");
      loadLive();
    } catch (err) { toast.error("Failed to reset break"); }
  };

  const loadLive = useCallback(async () => {
    setLoadingLive(true);
    try {
      const { data } = await api.get('/attendance');
      setEmployees(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load attendance dashboard');
    } finally {
      setLoadingLive(false);
    }
  }, []);

  // Load Staff List for History Filter
  useEffect(() => {
    api.get('/employees')
      .then(({ data }) => setAllEmployees(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Load History Logs
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const params = {};
      if (historyEmpId) params.employeeId = historyEmpId;
      if (startDate)    params.startDate  = startDate;
      if (endDate)      params.endDate    = endDate;

      const { data } = await api.get('/attendance/history', { params });
      setRecords(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load attendance logs');
    } finally {
      setLoadingHistory(false);
    }
  }, [historyEmpId, startDate, endDate]);

  useEffect(() => { loadLive(); }, [loadLive]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  const onlineCount  = employees.filter(e => e.onlineStatus === 'online').length;
  const offlineCount = employees.filter(e => e.onlineStatus !== 'online').length;

  return (
    <div className="att-page">
      {/* ── Page Toolbar: Summary & Refresh ── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <span className="badge rounded-pill" style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: '1px solid var(--primary-border)', padding: '6px 12px', fontSize: '12px', fontWeight: 600 }}>
            {employees.length} Team Members
          </span>
          <span className="text-muted small d-none d-md-inline">Real-time team presence & session tracking</span>
        </div>

        <div className="leads-actions">
          <button className="leads-btn outline" onClick={activeTab === 'live' ? loadLive : loadHistory}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Stats Summary Grid ── */}
      <div className="att-stat-grid">
        <div className="att-stat-card">
          <div className="att-stat-label">
            {user?.role === 'Agent' ? 'MY ATTENDANCE' : user?.role === 'Manager' ? 'TEAM STAFF' : 'TOTAL STAFF'}
          </div>
          <div className="att-stat-value neutral">{employees.length}</div>
        </div>
        <div className="att-stat-card">
          <div className="att-stat-label">Online Now</div>
          <div className="att-stat-value online">{onlineCount}</div>
        </div>
        <div className="att-stat-card">
          <div className="att-stat-label">Offline</div>
          <div className="att-stat-value offline">{offlineCount}</div>
        </div>
      </div>

      {/* ── Main Tabbed Panel ── */}
      <div className="tp-card" data-tour="attendance-tracker">
        <div className="tp-card__head" style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} style={{ borderBottom: 'none' }}>
            <Nav.Item>
              <Nav.Link eventKey="live" style={{ cursor: 'pointer' }}>
                <Users size={15} style={{ marginRight: 6, display: 'inline' }} /> Live Status
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="history" style={{ cursor: 'pointer' }}>
                <Clock size={15} style={{ marginRight: 6, display: 'inline' }} /> Attendance History
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        <div style={{ padding: '16px', borderTop: '1px solid var(--border)' }}>
          {/* TAB 1: Live Status Table */}
          {activeTab === 'live' && (
            loadingLive ? (
              <table className="tp-table">
                <thead>
                  <tr><th>Employee</th><th>Role</th><th>Status</th><th>Last Login</th><th>Last Active</th></tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4].map(n => (
                    <tr key={n}>
                      <td><div className="crm-skeleton-pulse" style={{ width: 130, height: 16 }} /></td>
                      <td><div className="crm-skeleton-pulse" style={{ width: 80, height: 14 }} /></td>
                      <td><div className="crm-skeleton-pulse" style={{ width: 70, height: 14 }} /></td>
                      <td><div className="crm-skeleton-pulse" style={{ width: 140, height: 14 }} /></td>
                      <td><div className="crm-skeleton-pulse" style={{ width: 140, height: 14 }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : employees.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>No staff logged.</div>
            ) : (
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map(emp => {
                    
                    return (
                      <tr key={emp.id || emp._id}>
                        <td style={{ fontWeight: 600, color: 'var(--text)' }}>{emp.name}</td>
                        <td style={{ color: 'var(--text-2)' }}>{emp.role}</td>
                        <td>
                          <span className={`staleness-dot ${emp.onlineStatus === 'online' ? 'green' : emp.onlineStatus?.toLowerCase() === 'break' ? 'amber' : 'red'}`} style={{ marginRight: '6px', display: 'inline-block' }} />
                          <span style={{ textTransform: 'capitalize' }}>{emp.onlineStatus || 'offline'}</span>
                        </td>
                        <td style={{ color: 'var(--muted)', fontSize: '12px' }}>
                          {emp.lastLoginAt ? new Date(emp.lastLoginAt).toLocaleString() : '—'}
                        </td>
                        <td style={{ color: 'var(--muted)', fontSize: '12px' }}>
                          {emp.lastSeenAt ? new Date(emp.lastSeenAt).toLocaleString() : '—'}
                        </td>
                        <td style={{ fontSize: '13px' }}>
                          <span className={emp.breakMins >= 60 ? 'text-danger fw-bold' : 'text-warning'}>
                            {emp.breakMins || 0} / 60 mins
                          </span>
                        </td>
                        {user?.role !== 'Agent' && (
                          <td>
                            <button 
                              className="btn btn-sm btn-outline-danger py-0 px-2"
                              style={{ fontSize: '11px' }}
                              onClick={() => handleResetBreak(emp.id)}
                            >
                              Reset
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          )}

          {/* TAB 2: Attendance History */}
          {activeTab === 'history' && (
            <div>
              {/* Filter Controls */}
              <div className="hist-filters" style={{ marginBottom: '16px' }}>
                {user?.role !== 'Agent' && (
                  <div className="hist-filter-group">
                    <label style={{ color: 'var(--muted)', fontSize: '12px', fontWeight: 600 }}>Employee</label>
                    <select
                      value={historyEmpId}
                      onChange={e => setHistoryEmpId(e.target.value)}
                      style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px 10px', fontSize: '13px' }}
                    >
                      <option value="">My History</option>
                      {allEmployees
                        .filter(e => user?.role === 'Admin' || e.reportingTo === user?.id)
                        .map(e => (
                          <option key={e.id || e._id} value={e.id || e._id}>{e.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="hist-filter-group">
                  <label style={{ color: 'var(--muted)', fontSize: '12px', fontWeight: 600 }}>Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px 10px', fontSize: '13px' }}
                  />
                </div>

                <div className="hist-filter-group">
                  <label style={{ color: 'var(--muted)', fontSize: '12px', fontWeight: 600 }}>End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px 10px', fontSize: '13px' }}
                  />
                </div>
              </div>

              {/* History Table */}
              {loadingHistory ? (
                <table className="tp-table">
                  <thead>
                    <tr><th>Date</th><th>Employee</th><th>Working Hours</th><th>Logout Type</th></tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4].map(n => (
                      <tr key={n}>
                        <td><div className="crm-skeleton-pulse" style={{ width: 100, height: 14 }} /></td>
                        <td><div className="crm-skeleton-pulse" style={{ width: 130, height: 16 }} /></td>
                        <td><div className="crm-skeleton-pulse" style={{ width: 80, height: 14 }} /></td>
                        <td><div className="crm-skeleton-pulse" style={{ width: 70, height: 20, borderRadius: 10 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : records.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>No attendance records found.</div>
              ) : (
                <table className="tp-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Employee</th>
                      <th>Login Time</th>
                      <th>Logout Time</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r, idx) => (
                      <tr key={r.id || r._id || idx}>
                        <td style={{ color: 'var(--text-2)' }}>{r.date || (r.loginTime ? new Date(r.loginTime).toLocaleDateString() : '—')}</td>
                        <td style={{ fontWeight: 600, color: 'var(--text)' }}>{r.employeeName || r.name || 'Staff'}</td>
                        <td style={{ color: 'var(--muted)', fontSize: '12px' }}>{r.loginTime ? new Date(r.loginTime).toLocaleTimeString() : '—'}</td>
                        <td style={{ color: 'var(--muted)', fontSize: '12px' }}>{r.logoutTime ? new Date(r.logoutTime).toLocaleTimeString() : 'Active'}</td>
                        <td style={{ fontWeight: 600, color: 'var(--primary)' }}>{r.duration ? `${r.duration} hrs` : 'Ongoing'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

